import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, FileDown, ShieldCheck, Thermometer, Snowflake, Gauge, Radio, CheckCircle2 } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import NabidkaAkci from '../layout/NabidkaAkci';

/**
 * Checklisty → Přehled: měření na měřicích bodech pro vedení.
 *
 * Nahoře měsíc, PDF a potvrzení měsíčního záznamu (HACCP). Pod tím karty
 * bodů — poslední hodnota, trend s pásmem limitu a stav (v pořádku,
 * odchylka, nezměřeno, čidlo neposílá). Dole odchylky se stavem nápravy;
 * vedení je uzavírá. Data: GET /api/mereni/prehled.
 */
const STAV = {
    ok:        { text: 'V pořádku', barva: '#10B981' },
    odchylka:  { text: 'Odchylka', barva: '#EF4444' },
    nezmereno: { text: 'Nezměřeno', barva: '#F59E0B' },
    cidlo:     { text: 'Čidlo neposílá', barva: '#F59E0B' },
};
const IKONA = { lednice: Thermometer, mrazak: Snowflake, meridlo: Gauge, jine: Gauge };
const cislo = (v) => v == null ? '—' : Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: 3 });
const datumCas = (iso) => iso ? new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const limitText = (b) => b.limit_min != null && b.limit_max != null ? `${cislo(b.limit_min)} až ${cislo(b.limit_max)}`
    : b.limit_max != null ? `≤ ${cislo(b.limit_max)}` : b.limit_min != null ? `≥ ${cislo(b.limit_min)}` : null;
const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

function Trend({ bod, barva, isDark }) {
    const body = bod.rada || [];
    if (body.length < 2) return <div style={{ height: 36, fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>Málo dat pro trend</div>;
    const hodnoty = body.map(r => r.h);
    let min = Math.min(...hodnoty), max = Math.max(...hodnoty);
    if (bod.limit_min != null) min = Math.min(min, bod.limit_min);
    if (bod.limit_max != null) max = Math.max(max, bod.limit_max);
    const rozpeti = max - min || 1;
    const y = (v) => 32 - ((v - min) / rozpeti) * 28 - 2;
    const x = (i) => (i / (body.length - 1)) * 200;
    const pasmoOd = bod.limit_max != null ? y(bod.limit_max) : 0;
    const pasmoDo = bod.limit_min != null ? y(bod.limit_min) : 36;
    return (
        <svg viewBox="0 0 200 36" preserveAspectRatio="none" style={{ width: '100%', height: 36, display: 'block' }} aria-hidden="true">
            {(bod.limit_min != null || bod.limit_max != null) && (
                <rect x="0" y={pasmoOd} width="200" height={Math.max(1, pasmoDo - pasmoOd)} fill={isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.12)'} />
            )}
            <polyline points={body.map((r, i) => `${x(i)},${y(r.h)}`).join(' ')} fill="none" stroke={barva} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
            {body.map((r, i) => r.x ? <circle key={i} cx={x(i)} cy={y(r.h)} r="2.4" fill="#EF4444" /> : null)}
        </svg>
    );
}

export default function MereniPrehled({ isDark, isMobile, barva = '#06B6D4', onNastavit }) {
    const [mesic, setMesic] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
    const [data, setData] = useState(null);
    const [vyrizuji, setVyrizuji] = useState(null);     // odchylka
    const [poznamka, setPoznamka] = useState('');
    const [potvrzuji, setPotvrzuji] = useState(false);
    const [odchylkyMobil, setOdchylkyMobil] = useState(false);   // §1g: K dořešení → modal

    const nacti = useCallback(() => {
        fetch(`/api/mereni/prehled?mesic=${mesic}`, { headers: { Accept: 'application/json' } })
            .then(r => r.ok ? r.json() : null).then(d => d && setData(d)).catch(() => {});
    }, [mesic]);
    useEffect(() => { nacti(); }, [nacti]);

    const posun = (s) => { const [r, m] = mesic.split('-').map(Number); const d = new Date(r, m - 1 + s, 1); setMesic(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
    const nazevMesice = (() => { const [r, m] = mesic.split('-').map(Number); return new Date(r, m - 1, 1).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' }); })();
    const hrana = isDark ? 'rgba(255,255,255,0.09)' : 'var(--border-default)';
    const plocha = isDark ? 'rgba(255,255,255,0.035)' : 'var(--surface-raised)';

    const post = async (url, telo) => {
        const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() }, body: JSON.stringify(telo) });
        return r.ok;
    };
    const vyridit = async () => {
        if (await post(`/api/mereni/${vyrizuji.id}/vyridit`, { poznamka })) { setVyrizuji(null); setPoznamka(''); nacti(); }
    };
    const potvrdit = async () => {
        if (await post('/api/mereni/potvrdit', { mesic, poznamka })) { setPotvrzuji(false); setPoznamka(''); nacti(); }
    };

    if (!data) return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>Načítám měření…</div>;

    const otevrene = data.odchylky.filter(o => !o.vyrizeno_at);
    const tlacitko = { display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${hrana}`, background: 'transparent', color: 'var(--text-primary)', textDecoration: 'none' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
            {/* Měsíc, PDF, potvrzení */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button type="button" onClick={() => posun(-1)} aria-label="Předchozí měsíc" style={{ ...tlacitko, border: 'none', padding: '0 6px' }}><ChevronLeft size={16} /></button>
                    <b style={{ fontSize: 15, minWidth: 120, textAlign: 'center', textTransform: 'capitalize' }}>{nazevMesice}</b>
                    <button type="button" onClick={() => posun(1)} aria-label="Další měsíc" style={{ ...tlacitko, border: 'none', padding: '0 6px' }}><ChevronRight size={16} /></button>
                </div>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {data.pocet_mereni} měření · {otevrene.length ? <b style={{ color: '#EF4444' }}>{otevrene.length} čeká na vedení</b> : 'žádná otevřená odchylka'}
                </span>
                <span style={{ flex: 1 }} />
                {isMobile ? (
                    <NabidkaAkci jenNabidka isMobile isDark={isDark} barva={barva} akce={[
                        { id: 'pdf', label: 'Stáhnout PDF', Icon: FileDown, onClick: () => { window.location.href = `/api/mereni/pdf?mesic=${mesic}`; } },
                        ...(!data.potvrzeni && data.muze_vyridit ? [{ id: 'potvrdit', label: 'Potvrdit měsíc', Icon: ShieldCheck, onClick: () => setPotvrzuji(true) }] : []),
                    ]} />
                ) : (<>
                <a href={`/api/mereni/pdf?mesic=${mesic}`} style={tlacitko}><FileDown size={15} /> PDF</a>
                {data.potvrzeni ? (
                    <span style={{ ...tlacitko, cursor: 'default', color: '#10B981', borderColor: 'rgba(16,185,129,0.4)' }}>
                        <ShieldCheck size={15} /> Potvrdil(a) {data.potvrzeni.kdo}, {datumCas(data.potvrzeni.kdy)}
                    </span>
                ) : data.muze_vyridit && (
                    <button type="button" onClick={() => setPotvrzuji(true)} style={{ ...tlacitko, background: barva, color: '#fff', border: 'none' }}>
                        <ShieldCheck size={15} /> Potvrdit měsíc
                    </button>
                )}
                </>)}
            </div>
            {isMobile && data.potvrzeni && (
                <p style={{ margin: '-8px 0 0', fontSize: 12, color: '#10B981' }}><ShieldCheck size={12} style={{ verticalAlign: '-2px' }} /> Potvrdil(a) {data.potvrzeni.kdo}, {datumCas(data.potvrzeni.kdy)}</p>
            )}
            {/* Mobil: odchylky přes řádek „K dořešení" do modalu (norma §1g) */}
            {isMobile && otevrene.length > 0 && (
                <button type="button" onClick={() => setOdchylkyMobil(true)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                    border: '1px solid rgba(239,68,68,0.45)', background: 'rgba(239,68,68,0.08)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                    <span style={{ flex: 1 }}>K dořešení · {otevrene.length}</span><ChevronRight size={16} />
                </button>
            )}

            {/* Měřicí body */}
            {data.body.length === 0 ? (
                <div style={{ border: `1px dashed ${hrana}`, borderRadius: 12, padding: 18, fontSize: 13.5, color: 'var(--text-secondary)' }}>
                    Zatím tu nejsou žádné měřicí body. Přidej lednice a mrazáky v záložce Měřicí body{onNastavit ? <> — <button type="button" onClick={onNastavit} style={{ background: 'none', border: 'none', color: barva, cursor: 'pointer', fontWeight: 600, padding: 0 }}>otevřít</button></> : null}.
                </div>
            ) : (
                isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {data.body.map(b => {
                            const s = STAV[b.stav] || STAV.ok;
                            const Ikona = b.cidlo ? Radio : (IKONA[b.typ] || Gauge);
                            return (
                                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 58, borderBottom: `1px solid ${hrana}` }}>
                                    <Ikona size={16} style={{ color: barva, flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <b style={{ fontSize: 14.5, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.nazev}</b>
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{[limitText(b) && `limit ${limitText(b)}`, b.posledni && datumCas(b.posledni.kdy)].filter(Boolean).join(' · ')}</span>
                                    </div>
                                    {b.stav !== 'ok'
                                        ? <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 99, color: s.barva, background: `${s.barva}1f`, whiteSpace: 'nowrap' }}>{s.text}</span>
                                        : <b style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{b.posledni ? `${cislo(b.posledni.hodnota)} ${b.jednotka || ''}` : '—'}</b>}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                    {data.body.map(b => {
                        const s = STAV[b.stav] || STAV.ok;
                        const Ikona = b.cidlo ? Radio : (IKONA[b.typ] || Gauge);
                        const lt = limitText(b);
                        return (
                            <div key={b.id} style={{ border: `1px solid ${b.stav === 'odchylka' ? 'rgba(239,68,68,0.5)' : hrana}`, background: plocha, borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Ikona size={14} style={{ color: barva, flexShrink: 0 }} />
                                    <b style={{ fontSize: 13, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.nazev}</b>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, color: s.barva, background: `${s.barva}1f`, whiteSpace: 'nowrap' }}>{s.text}</span>
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: b.stav === 'odchylka' ? '#EF4444' : 'var(--text-primary)' }}>
                                    {b.posledni ? cislo(b.posledni.hodnota) : '—'} <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>{b.jednotka}</span>
                                </div>
                                <Trend bod={b} barva={barva} isDark={isDark} />
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                    {[lt && `limit ${lt}`, b.frekvence === 'tydne' ? 'týdně' : b.frekvence === 'denne' ? 'denně' : b.cidlo ? 'čidlo' : null].filter(Boolean).join(' · ')}
                                    {b.posledni && <><br />naposledy {datumCas(b.posledni.kdy)}{b.posledni.kdo ? ` · ${b.posledni.kdo}` : ''}</>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                )
            )}

            {/* Odchylky (na mobilu v modalu přes „K dořešení") */}
            {data.odchylky.length > 0 && (!isMobile || odchylkyMobil) && (() => { const obsah = (
                <div style={{ border: `1px solid ${hrana}`, borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: `1px solid ${hrana}` }}>
                        Odchylky a nápravná opatření
                    </div>
                    {data.odchylky.map(o => (
                        <div key={o.id} style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${hrana}`, flexDirection: isMobile ? 'column' : 'row' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                                    {o.nazev} — {o.stav === 'vynechano' ? 'nezměřeno' : `${cislo(o.hodnota)} ${o.jednotka || ''}`}
                                    {o.podezrele && o.stav !== 'mimo' ? ' (nevěrohodný odečet)' : ''}
                                    {o.limit && o.stav === 'mimo' ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · limit {o.limit}</span> : null}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                    {datumCas(o.namereno_at)}{o.kdo ? ` · ${o.kdo}` : o.zdroj === 'cidlo' ? ' · čidlo' : ''}
                                    {(o.opatreni || o.opatreni_text) && <> · {[o.opatreni, o.opatreni_text].filter(Boolean).join(': ')}</>}
                                    {o.vyrizeno_at && <> · vyřídil(a) {o.vyresil}{o.vyrizeni_poznamka ? `: ${o.vyrizeni_poznamka}` : ''}</>}
                                </div>
                            </div>
                            {o.vyrizeno_at ? (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Vyřízeno</span>
                            ) : data.muze_vyridit ? (
                                <button type="button" onClick={() => { setVyrizuji(o); setPoznamka(''); }} style={{ ...tlacitko, height: 32 }}>Vyřídit</button>
                            ) : (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#EF4444' }}>Čeká na vedení</span>
                            )}
                        </div>
                    ))}
                </div>
            ); return isMobile ? (
                <ModalShell onClose={() => setOdchylkyMobil(false)} isDark={isDark} barva={barva} ikona={ShieldCheck} nadpis="K dořešení">
                    <div style={{ padding: 12 }}>{obsah}</div>
                </ModalShell>
            ) : obsah; })()}

            {(vyrizuji || potvrzuji) && (
                <ModalShell onClose={() => { setVyrizuji(null); setPotvrzuji(false); }} isDark={isDark} barva={barva} ikona={ShieldCheck}
                    nadpis={vyrizuji ? `Vyřídit: ${vyrizuji.nazev}` : `Potvrdit záznam — ${nazevMesice}`} sirka={460}
                    paticka={(
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => { setVyrizuji(null); setPotvrzuji(false); }} style={tlacitko}>Zrušit</button>
                            <button type="button" onClick={vyrizuji ? vyridit : potvrdit} style={{ ...tlacitko, background: barva, color: '#fff', border: 'none' }}>
                                {vyrizuji ? 'Vyřízeno' : 'Potvrdit'}
                            </button>
                        </div>
                    )}>
                    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>
                            {vyrizuji
                                ? 'Co se s odchylkou udělalo? Zapíše se do záznamu měření i do měsíčního PDF.'
                                : 'Potvrzuješ, že jsi záznam měření za tento měsíc zkontroloval(a). Jméno a datum se objeví v PDF.'}
                        </p>
                        <textarea value={poznamka} onChange={e => setPoznamka(e.target.value)} rows={3}
                            placeholder={vyrizuji ? 'např. servis vyměnil těsnění, přeměřeno 3,8 °C' : 'Poznámka (volitelné)'}
                            style={{ width: '100%', borderRadius: 10, border: `1px solid ${hrana}`, background: 'transparent', color: 'var(--text-primary)', padding: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
                    </div>
                </ModalShell>
            )}
        </div>
    );
}
